import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { select, zoomIdentity } from "d3";

import { webappURL } from "../../util/url";
import { isMobile as isMobileUserAgent } from "../../util/navigator";
import useBrandRelations, {
  transformToLegacyFormat,
  buildAdjacencyMapsFromEdges,
  buildBrandLogoMapFromEdges,
} from "../../query/useBrandRelations";
import useBrandRadarGraph from "./useBrandRadarGraph";
import BrandRadarHeader from "./components/BrandRadarHeader";
import BrandRadarSearch from "./components/BrandRadarSearch";
import BrandRadarBreadcrumb from "./components/BrandRadarBreadcrumb";
import BrandRadarLegend from "./components/BrandRadarLegend";
import BrandRadarInfoPanel from "./components/BrandRadarInfoPanel";
import BrandRadarControls from "./components/BrandRadarControls";
import BrandRadarStats from "./components/BrandRadarStats";
import BrandRadarLoading from "./components/BrandRadarLoading";
import BrandRadarError from "./components/BrandRadarError";

import "./BrandRadar.css";

const DEFAULT_CENTER = "Nike";

const SITE_NAME = "후루츠패밀리";
const SITE_ALTERNATE_NAME = "FruitsFamily";
const BRAND_RADAR_NAME = "후루츠패밀리 브랜드 레이더";
const DEFAULT_META_TITLE =
  "브랜드 레이더 | 후루츠패밀리 - 패션 브랜드 네트워크 탐색";
const DEFAULT_META_DESCRIPTION =
  "좋아하는 패션 브랜드와 닮은 새로운 브랜드를 후루츠패밀리 브랜드 레이더에서 발견해보세요. 인터랙티브 네트워크 그래프로 브랜드 간의 관계를 한눈에 탐색할 수 있습니다.";
const DEFAULT_META_KEYWORDS =
  "브랜드 추천, 패션 브랜드, 비슷한 브랜드 찾기, 브랜드 네트워크, Nike, Adidas, Stussy, Supreme, Chrome Hearts, Rick Owens, Comme des Garcons, The North Face, Bape, Carhartt, Polo Ralph Lauren, Levi's, New Balance, ARCTERYX, 빈티지 브랜드, 스트릿 브랜드, 디자이너 브랜드, 후루츠패밀리";
const WEB_APP_SCHEMA_DESCRIPTION =
  "패션 브랜드 간의 유사도를 인터랙티브 네트워크 그래프로 시각화해, 좋아하는 브랜드로부터 연결된 새로운 취향의 브랜드를 발견할 수 있는 후루츠패밀리의 탐색 도구.";

/**
 * 브랜드 레이더 페이지
 * @param hostname
 * @param userAgent
 * @returns {JSX.Element}
 * @constructor
 */
export default function BrandRadar({ hostname, userAgent }) {
  const { brandName: urlBrandName } = useParams();
  const navigate = useNavigate();

  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const navigateToBrandRef = useRef(null);
  const layoutRef = useRef({
    width: 0,
    height: 0,
    isMobile: isMobileUserAgent(userAgent),
  });

  // Determine initial brand from URL or default
  const getInitialBrand = useCallback(() => {
    if (urlBrandName) {
      return decodeURIComponent(urlBrandName);
    }
    return DEFAULT_CENTER;
  }, [urlBrandName]);

  const [currentCenter, setCurrentCenter] = useState(getInitialBrand);
  const [trail, setTrail] = useState(() => [getInitialBrand()]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [graphStats, setGraphStats] = useState({ nodes: 0, links: 0 });
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isMobile, setIsMobile] = useState(() => isMobileUserAgent(userAgent));
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Sync URL param -> state (handles browser back/forward, external links)
  useEffect(() => {
    const target = urlBrandName
      ? decodeURIComponent(urlBrandName)
      : DEFAULT_CENTER;
    if (target === currentCenter) return;
    setCurrentCenter(target);
    setSelectedBrand(null);
    setTrail((prev) => {
      const idx = prev.indexOf(target);
      if (idx >= 0) return prev.slice(0, idx + 1);
      const next = [...prev, target];
      return next.length > 6 ? next.slice(-6) : next;
    });
  }, [urlBrandName, currentCenter]);

  // Fetch brand relations from API
  const {
    relations: apiRelations,
    loading,
    error,
  } = useBrandRelations({
    brandName: currentCenter,
  });

  // Transform API data
  const BRAND_RELATIONS = useMemo(
    () => transformToLegacyFormat(apiRelations),
    [apiRelations],
  );

  const { dirSim, adj } = useMemo(
    () => buildAdjacencyMapsFromEdges(apiRelations),
    [apiRelations],
  );

  const brandLogos = useMemo(
    () => buildBrandLogoMapFromEdges(apiRelations),
    [apiRelations],
  );

  // Lock page scroll while the radar owns the full viewport.
  useEffect(() => {
    document.body.classList.add("brand-radar-page");
    return () => {
      document.body.classList.remove("brand-radar-page");
    };
  }, []);

  // Update dimensions on resize. Debounced to avoid rebuilding D3 while the
  // browser window is still being dragged or mobile keyboard height changes.
  useEffect(() => {
    let resizeTimeoutId;

    function updateDimensions({ force = false } = {}) {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const nextIsMobile = width < 768;
      const previous = layoutRef.current;
      const widthChanged = width !== previous.width;
      const heightChanged = Math.abs(height - previous.height) > 120;
      const breakpointChanged = nextIsMobile !== previous.isMobile;
      const searchFocused = document.activeElement?.closest(".BrandRadar-search");

      if (
        !force &&
        !widthChanged &&
        searchFocused
      ) {
        return;
      }

      if (
        !force &&
        !widthChanged &&
        !heightChanged &&
        !breakpointChanged
      ) {
        return;
      }

      layoutRef.current = { width, height, isMobile: nextIsMobile };
      setDimensions((prev) =>
        prev.width === width && prev.height === height ? prev : { width, height },
      );
      setIsMobile((prev) => (prev === nextIsMobile ? prev : nextIsMobile));
    }

    function scheduleDimensionsUpdate() {
      window.clearTimeout(resizeTimeoutId);
      resizeTimeoutId = window.setTimeout(() => updateDimensions(), 180);
    }

    updateDimensions({ force: true });
    window.addEventListener("resize", scheduleDimensionsUpdate);
    return () => {
      window.clearTimeout(resizeTimeoutId);
      window.removeEventListener("resize", scheduleDimensionsUpdate);
    };
  }, []);

  // Get similar brands for info panel
  const getSimilarBrands = useCallback(
    (brandName) => {
      let similar = [];
      BRAND_RELATIONS.forEach(([s, t, lv]) => {
        if (s === brandName && lv === 1) similar.push(t);
      });
      if (!similar.length) {
        BRAND_RELATIONS.forEach(([s, t, lv]) => {
          if (s === brandName && lv === 2 && similar.length < 10)
            similar.push(t);
        });
      }
      if (!similar.length && adj[brandName]) {
        similar = [...adj[brandName]].slice(0, 10);
      }
      return similar;
    },
    [adj, BRAND_RELATIONS],
  );

  // Navigate to brand
  const navigateToBrand = useCallback(
    (brandName) => {
      if (brandName === currentCenter) return;

      setSelectedBrand({ id: brandName });

      if (adj[brandName] && adj[brandName].size > 0) {
        setTrail((prev) => {
          const idx = prev.indexOf(brandName);
          if (idx >= 0) {
            return prev.slice(0, idx + 1);
          }
          const newTrail = [...prev, brandName];
          return newTrail.length > 6 ? newTrail.slice(-6) : newTrail;
        });
        setCurrentCenter(brandName);
        navigate(`/brand-radar/${encodeURIComponent(brandName)}`, {
          replace: true,
        });
      }
    },
    [currentCenter, adj, navigate],
  );

  // Keep ref updated with latest navigateToBrand
  useEffect(() => {
    navigateToBrandRef.current = navigateToBrand;
  }, [navigateToBrand]);

  // Render D3 graph
  const { zoomRef } = useBrandRadarGraph({
    svgRef,
    dimensions,
    loading,
    currentCenter,
    relations: BRAND_RELATIONS,
    dirSim,
    adj,
    brandLogos,
    isMobile,
    navigateToBrandRef,
    onStatsChange: setGraphStats,
    onSelectBrand: setSelectedBrand,
  });

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    if (zoomRef.current && svgRef.current) {
      select(svgRef.current)
        .transition()
        .duration(200)
        .call(zoomRef.current.scaleBy, 1.4);
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    if (zoomRef.current && svgRef.current) {
      select(svgRef.current)
        .transition()
        .duration(200)
        .call(zoomRef.current.scaleBy, 0.7);
    }
  }, []);

  const handleReset = useCallback(() => {
    if (zoomRef.current && svgRef.current) {
      const { width, height } = dimensions;
      const initialScale = isMobile ? 0.6 : 0.8;
      select(svgRef.current)
        .transition()
        .duration(500)
        .call(
          zoomRef.current.transform,
          zoomIdentity
            .translate(width / 2, height / 2)
            .scale(initialScale)
            .translate(-width / 2, -height / 2),
        );
    }
  }, [dimensions, isMobile]);

  // Search
  const handleSearchSelect = useCallback(
    (brandName) => {
      setTrail([brandName]);
      setCurrentCenter(brandName);
      navigate(`/brand-radar/${encodeURIComponent(brandName)}`, {
        replace: true,
      });
    },
    [navigate],
  );

  const handleBreadcrumbNavigate = useCallback(
    (name, index) => {
      setTrail((prev) => prev.slice(0, index + 1));
      setCurrentCenter(name);
      navigate(`/brand-radar/${encodeURIComponent(name)}`, { replace: true });
    },
    [navigate],
  );

  // Similar brands for info panel
  const similarBrands = useMemo(() => {
    if (!selectedBrand) return [];
    return getSimilarBrands(selectedBrand.id);
  }, [selectedBrand, getSimilarBrands]);

  // Meta tags (SEO) - all static-or-brand-derived values memoized together
  const seo = useMemo(() => {
    const webUrl = webappURL(hostname);
    const isDefaultBrand = currentCenter === DEFAULT_CENTER;
    const metaImage = `${webUrl}/logo512.png`;
    const metaUrl = isDefaultBrand
      ? `${webUrl}/brand-radar`
      : `${webUrl}/brand-radar/${encodeURIComponent(currentCenter)}`;

    const webApplicationSchema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: BRAND_RADAR_NAME,
      alternateName: "Brand Radar",
      url: `${webUrl}/brand-radar`,
      description: WEB_APP_SCHEMA_DESCRIPTION,
      applicationCategory: "ShoppingApplication",
      operatingSystem: "Any",
      inLanguage: "ko-KR",
      isAccessibleForFree: true,
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        alternateName: SITE_ALTERNATE_NAME,
        url: webUrl,
        logo: { "@type": "ImageObject", url: metaImage },
      },
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "홈", item: webUrl },
        {
          "@type": "ListItem",
          position: 2,
          name: "브랜드 레이더",
          item: `${webUrl}/brand-radar`,
        },
        ...(isDefaultBrand
          ? []
          : [
              {
                "@type": "ListItem",
                position: 3,
                name: currentCenter,
                item: metaUrl,
              },
            ]),
      ],
    };

    return {
      metaTitle: isDefaultBrand
        ? DEFAULT_META_TITLE
        : `${currentCenter} 비슷한 브랜드 추천 | 후루츠패밀리 브랜드 레이더`,
      metaDescription: isDefaultBrand
        ? DEFAULT_META_DESCRIPTION
        : `${currentCenter}와 닮은 스타일의 패션 브랜드를 후루츠패밀리 브랜드 레이더에서 한눈에 확인하세요. 연결된 유사 브랜드를 탐색하고 새로운 취향을 발견해보세요.`,
      metaKeywords: isDefaultBrand
        ? DEFAULT_META_KEYWORDS
        : `${currentCenter}, ${currentCenter} 비슷한 브랜드, ${currentCenter} 추천, 패션 브랜드, 브랜드 탐색, Chrome Hearts, Rick Owens, Comme des Garcons, Nike, Adidas, Supreme, 후루츠패밀리`,
      metaImage,
      metaUrl,
      webApplicationSchemaJson: JSON.stringify(webApplicationSchema),
      breadcrumbSchemaJson: JSON.stringify(breadcrumbSchema),
    };
  }, [hostname, currentCenter]);

  return (
    <div className="BrandRadar" ref={containerRef}>
      <Helmet>
        <html lang="ko" />
        <title>{seo.metaTitle}</title>
        <meta name="description" content={seo.metaDescription} />
        <meta name="keywords" content={seo.metaKeywords} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={seo.metaUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:locale" content="ko_KR" />
        <meta property="og:title" content={seo.metaTitle} />
        <meta property="og:description" content={seo.metaDescription} />
        <meta property="og:image" content={seo.metaImage} />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:alt" content={BRAND_RADAR_NAME} />
        <meta property="og:url" content={seo.metaUrl} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@fruitsfamilyco" />
        <meta name="twitter:title" content={seo.metaTitle} />
        <meta name="twitter:description" content={seo.metaDescription} />
        <meta name="twitter:image" content={seo.metaImage} />
        <meta name="twitter:image:alt" content={BRAND_RADAR_NAME} />

        <script type="application/ld+json">
          {seo.webApplicationSchemaJson}
        </script>
        <script type="application/ld+json">{seo.breadcrumbSchemaJson}</script>
      </Helmet>

      <div className="BrandRadar-glow" />

      <BrandRadarHeader />

      {mounted && (
        <>
          <BrandRadarSearch onSelectBrand={handleSearchSelect} />

          <BrandRadarBreadcrumb
            trail={trail}
            onNavigate={handleBreadcrumbNavigate}
          />

          {!isMobile && <BrandRadarLegend />}

          <BrandRadarInfoPanel
            selectedBrand={selectedBrand}
            similarBrands={similarBrands}
            onNavigateToBrand={navigateToBrand}
            isMobile={isMobile}
          />

          <BrandRadarControls
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onReset={handleReset}
          />

          <BrandRadarStats nodes={graphStats.nodes} links={graphStats.links} />

          {loading && <BrandRadarLoading />}

          {error && !loading && <BrandRadarError />}

          <svg ref={svgRef} className="BrandRadar-svg" />
        </>
      )}
    </div>
  );
}

BrandRadar.propTypes = {
  hostname: PropTypes.string.isRequired,
  userAgent: PropTypes.string,
};
